import { Link, useNavigate } from "react-router";

import { BrandArrow } from "@/components/brand-arrow";
import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Multi-Track Project Tracker";

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-card flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="/brand/logo/BOI_Logo_Black_RGB.svg"
            alt="Board of Innovation"
            className="h-7 w-auto object-contain"
          />
          <div>
            <p className="text-foreground text-base font-medium">{PROJECT_NAME}</p>
            <p className="text-muted-foreground text-xs">
              Connected weekly reporting across consulting teams
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm">
              Dashboard
            </Button>
          </Link>
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profile
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/tracks">
                <Button variant="ghost" size="sm">
                  Tracks
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activity
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-12">
        <section className="bg-boi-dark text-primary-foreground rounded-md p-10">
          <p className="text-accent-on-dark mb-3 text-sm font-medium uppercase">
            AI-powered weekly status orchestration
          </p>
          <h2 className="max-w-3xl text-4xl leading-tight">
            Turn scattered meeting notes into{" "}
            <span className="text-accent-on-dark">polished, cross-team project updates.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-white/80">
            Built for fast-moving consulting engagements that need consistent, well-structured
            reporting the whole team can trust.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button>
              Review this week&apos;s summary
              <BrandArrow />
            </Button>
            <Button
              variant="outline"
              className="border-white/30 bg-transparent text-white hover:bg-white/10"
            >
              Explore engagement activity
            </Button>
          </div>
        </section>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <div className="bg-card border-border rounded-md border p-6">
            <p className="text-primary text-sm font-medium">Capture</p>
            <h3 className="mt-2 text-lg">Meeting notes in, structure out</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              Convert partner, PMO, and workstream notes into a shared weekly narrative.
            </p>
          </div>
          <div className="bg-card border-border rounded-md border p-6">
            <p className="text-primary text-sm font-medium">Review</p>
            <h3 className="mt-2 text-lg">Flag risks before publication</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              Surface blockers, confidence gaps, and missing owners before updates go live.
            </p>
          </div>
          <div className="bg-card border-border rounded-md border p-6">
            <p className="text-primary text-sm font-medium">Publish</p>
            <h3 className="mt-2 text-lg">Distribute polished updates fast</h3>
            <p className="text-muted-foreground mt-2 text-sm">
              Keep leadership and client teams aligned with consistent, branded weekly reporting.
            </p>
          </div>
        </div>
        <p className="text-muted-foreground mt-6 text-sm">
          Backend status:{" "}
          <span className={backendStatus === "ok" ? "text-rag-green" : "text-rag-red"}>
            {backendStatus}
          </span>
        </p>
      </main>
    </div>
  );
}
